import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  Typography,
  Avatar,
  Grow,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Pets as PetsIcon,
  Forest as ForestIcon,
  Nightlife as NightlifeIcon,
  DirectionsCar as VehicleIcon,
  Person as PersonIcon,
  WbSunny as WeatherIcon,
  Warning as FireIcon,
  FilterAlt as FilterIcon,
  BugReport as AnimalIcon,
  Help as HelpIcon,
} from "@mui/icons-material";
import axios from "axios";
import { backend_url } from "../../../App";

export default function SpeciesCount() {
  const [speciesData, setSpeciesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Map species names to icons
  const getIcon = (name) => {
    const lower = name.toLowerCase();
    if (lower.includes("leopard")) return <PetsIcon />;
    if (lower.includes("fox")) return <ForestIcon />;
    if (lower.includes("snow leopard")) return <NightlifeIcon />;
    if (lower.includes("vehicle")) return <VehicleIcon />;
    if (lower.includes("person")) return <PersonIcon />;
    if (lower.includes("rain")) return <WeatherIcon />;
    if (lower.includes("fire")) return <FireIcon />;
    if (lower.includes("filtered")) return <FilterIcon />;
    if (lower.includes("animal") || lower.includes("ibex"))
      return <AnimalIcon />;
    if (lower.includes("n/a") || lower === "n/a") return <HelpIcon />;
    return <PetsIcon />;
  };

  // Map species names to colors
  const getColor = (name) => {
    const lower = name.toLowerCase();
    if (lower.includes("leopard")) return "#FF6B35";
    if (lower.includes("fox")) return "#FF8C42";
    if (lower.includes("snow leopard")) return "#795548";
    if (lower.includes("vehicle")) return "#2196F3";
    if (lower.includes("person")) return "#E91E63";
    if (lower.includes("rain")) return "#00BCD4";
    if (lower.includes("fire")) return "#FF5722";
    if (lower.includes("filtered")) return "#9C27B0";
    if (lower.includes("animal") || lower.includes("ibex")) return "#FF9800";
    if (lower.includes("n/a") || lower === "n/a") return "#757575";
    return "#757575";
  };

  useEffect(() => {
    const fetchSpeciesCounts = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("No authentication token found");
          setLoading(false);
          return;
        }

        const config = {
          headers: {
            Authorization: `Token ${token}`,
          },
        };

        // Fetch all species
        const speciesResponse = await axios.get(
          `${backend_url}/core/api/specie/?page_size=100`,
          config,
        );

        const allSpecies = speciesResponse.data.results || [];

        // Fetch event counts for each species
        const speciesWithCounts = await Promise.all(
          allSpecies.map(async (species) => {
            try {
              // Get count of events for this species
              const eventsResponse = await axios.get(
                `${backend_url}/core/api/event/?species=${species.id}&page_size=1`,
                config,
              );
              return {
                name: species.name,
                id: species.id,
                count: eventsResponse.data.count || 0,
                color: getColor(species.name),
                icon: getIcon(species.name),
              };
            } catch (err) {
              return {
                name: species.name,
                id: species.id,
                count: 0,
                color: getColor(species.name),
                icon: getIcon(species.name),
              };
            }
          }),
        );

        // Allowed species list (9 + N/A)
        const allowedSpecies = [
          "Vehicle",
          "Rain",
          "Person",
          "Leopard",
          "ibex",
          "fox",
          "Fire",
          "filtered",
          "Animal",
          "N/A",
        ];

        const filteredSpecies = speciesWithCounts.filter((species) =>
          allowedSpecies.includes(species.name),
        );

        // Sort by count descending
        const sortedSpecies = filteredSpecies.sort((a, b) => b.count - a.count);

        setSpeciesData(sortedSpecies);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching species data:", err);
        setError("Failed to load species data");
        setLoading(false);
      }
    };

    fetchSpeciesCounts();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          py: 8,
          background: "linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 100%)",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
        id="species-count"
      >
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          py: 8,
          background: "linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 100%)",
          position: "relative",
        }}
        id="species-count"
      >
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        py: 8,
        background: "linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 100%)",
        position: "relative",
      }}
      id="species-count"
    >
      <Container maxWidth="xl">
        <Typography
          variant="h3"
          fontWeight={700}
          gutterBottom
          sx={{
            textAlign: "center",
            mb: 2,
            color: "#fff",
            fontSize: { xs: "2rem", md: "3rem" },
          }}
        >
          Species Detected
        </Typography>
        <Typography
          variant="body1"
          sx={{
            textAlign: "center",
            mb: 5,
            color: "rgba(255,255,255,0.7)",
            maxWidth: 600,
            mx: "auto",
          }}
        >
          Tracking and monitoring wildlife species across the region
        </Typography>

        <Grid container spacing={3}>
          {speciesData.map((species, index) => (
            <Grid item xs={6} sm={4} md={2.4} key={species.id || index}>
              <Grow in timeout={800 + index * 100}>
                <Card
                  sx={{
                    p: 3,
                    textAlign: "center",
                    borderRadius: 4,
                    transition: "all 0.4s ease",
                    background:
                      "linear-gradient(135deg, rgba(26,26,26,0.95) 0%, rgba(45,45,45,0.95) 100%)",
                    border: `1px solid ${species.color}40`,
                    "&:hover": {
                      transform: "translateY(-8px) scale(1.05)",
                      boxShadow: `0 20px 50px ${species.color}40`,
                      borderColor: species.color,
                    },
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: -50,
                      right: -50,
                      width: 100,
                      height: 100,
                      borderRadius: "50%",
                      background: `${species.color}20`,
                    }}
                  />
                  <Avatar
                    sx={{
                      mx: "auto",
                      mb: 2,
                      bgcolor: species.color,
                      width: 56,
                      height: 56,
                      boxShadow: `0 8px 30px ${species.color}50`,
                    }}
                  >
                    {species.icon}
                  </Avatar>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ color: "#fff" }}
                  >
                    {species.name}
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ color: species.color }}
                  >
                    {species.count}+
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.5)" }}
                  >
                    Detected
                  </Typography>
                </Card>
              </Grow>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
